import { Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { getLocationForBusinessDataApi } from 'SupabseHandler';
import FormInputLabel from 'common/FormInputLabel';
import Availability from 'pages/Business/Provider/ProviderPopup/Partials/Availability';
import { theme } from 'theme';
import AddLocationPopup from 'pages/Business/Location/AddLocationPopup';

import { businessData as businessDataAtom, locationDataList, selectedLocation as selectedLocationAtom } from 'atoms';
import { useRecoilState } from 'recoil';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'context/AuthContext';

export default function AvailabilityForm({
    formData,
    formState,
    handleChange,
    additionalMultipleFields,
    setAdditionalMultipleFields,
}) {
    const { token } = useAuth();
    const [businessData] = useRecoilState(businessDataAtom);
    const [locationFormData, setLocationFormData] = useRecoilState(locationDataList);
    const [selectedLocation, setSelectedLocation] = useRecoilState(selectedLocationAtom);

    const [location, setLocation] = useState([]);
    const [loading, setLoading] = useState(false);
    const [extraAvailabilityCount, setExtraAvailabilityCount] = useState(0);

    const [extraAvailabilityWeekDay, setExtraAvailabilityWeekDay] = useState([]);
    const [extraAvailabilityStartTime, setExtraAvailabilityStartTime] = useState([]);
    const [extraAvailabilityEndTime, setExtraAvailabilityEndTime] = useState([]);
    const [extraAvailabilityLocation, setExtraAvailabilityLocation] = useState([]);
    const [extraAvailabilityTelemedecine, setExtraAvailabilityTelemedecine] = useState([]);
    const [showDropDown, setShowDropDown] = useState(false);
    const hasInitializedRef = useRef(false);
    
    // State for location popup
    const [openLocationPopup, setOpenLocationPopup] = useState(false);
    const [locationCountBeforeAdd, setLocationCountBeforeAdd] = useState(0);
    const [pendingAvailabilityIndex, setPendingAvailabilityIndex] = useState(null);

    // Function to ensure all arrays have values at every index up to the given count
    const ensureArrayLengths = (count) => {
        // Create new arrays with at least 'count + 1' elements
        const padArray = (arr, length) => {
            const newArr = Array.isArray(arr) ? [...arr] : [];
            while (newArr.length <= length) {
                newArr.push(''); // Add empty values to ensure length
            }
            return newArr;
        };
        
        // Make sure all arrays have at least 'count + 1' elements
        const newWeekDays = padArray(extraAvailabilityWeekDay, count);
        const newStartTimes = padArray(extraAvailabilityStartTime, count);
        const newEndTimes = padArray(extraAvailabilityEndTime, count);
        const newLocations = padArray(extraAvailabilityLocation, count);
        
        // For telemedicine, we use false as default
        const newTelemedicine = Array.isArray(extraAvailabilityTelemedecine) ? 
            [...extraAvailabilityTelemedecine] : [];
        while (newTelemedicine.length <= count) {
            newTelemedicine.push(false);
        }
        
        // Update state variables
        setExtraAvailabilityWeekDay(newWeekDays);
        setExtraAvailabilityStartTime(newStartTimes);
        setExtraAvailabilityEndTime(newEndTimes);
        setExtraAvailabilityLocation(newLocations);
        setExtraAvailabilityTelemedecine(newTelemedicine);
        
        // Update additionalMultipleFields
        setAdditionalMultipleFields(prev => ({
            ...prev,
            availability_day: newWeekDays,
            availability_start_time: newStartTimes,
            availability_end_time: newEndTimes,
            availability_location: newLocations,
            telemedicine: newTelemedicine,
            form_section: 'availability',
        }));
    };

    const handelAvailabilityChange = (index) => {
        return (e) => {
            let { name, value, checked } = e.target;
            if (name === 'week_day') {
                let weekDays = Array.isArray(extraAvailabilityWeekDay) ? [...extraAvailabilityWeekDay] : [];
                weekDays[index] = value;
                setExtraAvailabilityWeekDay(weekDays);

                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_day: weekDays,
                }));
            }

            if (name === 'start_time') {
                let startTime = Array.isArray(extraAvailabilityStartTime) ? [...extraAvailabilityStartTime] : [];
                startTime[index] = value;
                setExtraAvailabilityStartTime(startTime);

                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_start_time: startTime,
                }));
            }

            if (name === 'end_time') {
                let endTime = Array.isArray(extraAvailabilityEndTime) ? [...extraAvailabilityEndTime] : [];
                endTime[index] = value;
                setExtraAvailabilityEndTime(endTime);

                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_end_time: endTime,
                }));
            }

            if (name === 'location') {
                let location = Array.isArray(extraAvailabilityLocation) ? [...extraAvailabilityLocation] : [];
                location[index] = value;
                setExtraAvailabilityLocation(location);

                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_location: location,
                }));
            }
            
            if (name === 'telemedicine') {
                let telemedicine = Array.isArray(extraAvailabilityTelemedecine) ? [...extraAvailabilityTelemedecine] : [];
                telemedicine[index] = checked;
                setExtraAvailabilityTelemedecine(telemedicine);

                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    telemedicine: telemedicine,
                }));
            }
        };
    };
    const getLocationForBusiness = async () => {
        setLoading(true);
        try {
            if (!token) {
                throw new Error('No authentication token found');
            }
            const data = await getLocationForBusinessDataApi(businessData?.id, token);
            // Preserve object structure - do not convert to array
            const locationData = Array.isArray(data) ? data : (data ? [data] : []);
            const loc = locationData.map((d) => ({
                id: d.id,
                location: d.name + ' ' + d.address + ' ' + d.city + ' ' + d.state + ' ' + d.suite + ' ' + d.zip,
            }));
            setLocation(loc);
            return loc;
 
        } catch (err) {
            console.error('Error fetching locations:', err);
            // Always set location to an array, even on error
            setLocation([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Handler to open location popup
    const handleAddLocationClick = (availabilityIndex) => {
        // Store the current location count before opening popup
        setLocationCountBeforeAdd(location.length);
        // Store which availability index triggered this
        setPendingAvailabilityIndex(availabilityIndex);
        // Reset location form data for new location
        setLocationFormData([{
            location_name: '',
            location_phone_no: '',
            location_address: '',
            city: '',
            state: '',
            zipCode: '',
            suite: ''
        }]);
        setSelectedLocation(null);
        // Open the popup
        setOpenLocationPopup(true);
    };

    // Handler to close location popup and refresh locations
    const handleCloseLocationPopup = async () => {
        setOpenLocationPopup(false);
        
        // Refresh locations after closing
        const updatedLocations = await getLocationForBusiness();
        
        // Check if a new location was added
        if (updatedLocations.length > locationCountBeforeAdd && pendingAvailabilityIndex !== null) {
            const newLocation = updatedLocations[updatedLocations.length - 1];
            
            // Scenario a: If business had no other locations, select the new one
            if (locationCountBeforeAdd === 0) {
                let locationArray = Array.isArray(extraAvailabilityLocation) ? [...extraAvailabilityLocation] : [];
                locationArray[pendingAvailabilityIndex] = newLocation.id;
                setExtraAvailabilityLocation(locationArray);
                
                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_location: locationArray,
                }));
            }
            // Scenario b: If there were other locations, the new location is already in the dropdown
            // User can manually select it if they want
        }
        
        // Reset pending state
        setPendingAvailabilityIndex(null);
        setLocationCountBeforeAdd(0);
    };

    useEffect(() => {
        setAdditionalMultipleFields(prev => ({
            ...prev,
            form_section: 'availability',
        }));
        getLocationForBusiness();
        // Reset initialization flag when component mounts
        hasInitializedRef.current = false;
    }, []);

    // Initialize availability data from formData or additionalMultipleFields
    // Only run on initial load to prevent overwriting user changes
    useEffect(() => {
        // Skip if we've already initialized
        if (hasInitializedRef.current) return;

        // First, try to load from additionalMultipleFields (most up-to-date)
        if (additionalMultipleFields?.availability_day && Array.isArray(additionalMultipleFields.availability_day) && additionalMultipleFields.availability_day.length > 0) {
            const weekDays = additionalMultipleFields.availability_day;
            const startTime = Array.isArray(additionalMultipleFields.availability_start_time) ? additionalMultipleFields.availability_start_time : [];
            const endTime = Array.isArray(additionalMultipleFields.availability_end_time) ? additionalMultipleFields.availability_end_time : [];
            const location = Array.isArray(additionalMultipleFields.availability_location) ? additionalMultipleFields.availability_location : [];
            const telemdecine = Array.isArray(additionalMultipleFields.telemedicine) ? additionalMultipleFields.telemedicine : [];
            
            if (weekDays.length > 0) {
                setExtraAvailabilityCount(Math.max(0, weekDays.length - 1));
                setExtraAvailabilityWeekDay(weekDays);
                setExtraAvailabilityStartTime(startTime);
                setExtraAvailabilityEndTime(endTime);
                setExtraAvailabilityLocation(location);
                setExtraAvailabilityTelemedecine(telemdecine);
                hasInitializedRef.current = true;
                return;
            }
        }

        // Fallback to formData (for edit mode initial load)
        if (formState === 'edit' && formData.availability) {
            let weekDays = Array.isArray(formData.availability_day) ? formData.availability_day : [];
            let startTime = Array.isArray(formData.availability_start_time) ? formData.availability_start_time : [];
            let endTime = Array.isArray(formData.availability_end_time) ? formData.availability_end_time : [];
            let location = Array.isArray(formData.availability_location) ? formData.availability_location : [];
            const telemdecine = Array.isArray(formData.telemedicine) ? formData.telemedicine : [];
            
            if (weekDays.length > 0) {
                setExtraAvailabilityCount(Math.max(0, weekDays.length - 1));
                setExtraAvailabilityWeekDay(weekDays);
                setExtraAvailabilityStartTime(startTime);
                setExtraAvailabilityEndTime(endTime);
                setExtraAvailabilityLocation(location);
                setExtraAvailabilityTelemedecine(telemdecine);

                // Sync the full set of arrays into additionalMultipleFields so that
                // form validation (which reads availability from additionalMultipleFields)
                // sees the existing data immediately. Without this, editing a single
                // field only writes that one array and validation keeps failing until
                // "Add Another Availability" pushes all arrays at once.
                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    availability_day: weekDays,
                    availability_start_time: startTime,
                    availability_end_time: endTime,
                    availability_location: location,
                    telemedicine: telemdecine,
                    form_section: 'availability',
                }));
                hasInitializedRef.current = true;
            }
        } else if (formState === 'add') {
            // For add mode, mark as initialized even if no data
            hasInitializedRef.current = true;
        }
    }, [formData, formState, additionalMultipleFields]);

    useEffect(() => {
        if (formState === 'add') {
            setShowDropDown(true);
        }
    }, []);

    useEffect(() => {
 
 
 

        setShowDropDown(true);
    }, [extraAvailabilityCount, extraAvailabilityWeekDay, extraAvailabilityStartTime, extraAvailabilityLocation]);

    // Check if either in-person or telemedicine consultation is configured or checkbox is checked
    const hasInPersonConsultation = !!(formData?.in_person_consultation_duration && formData?.in_person_consultation_price);
    const hasTelemedicineConsultation = !!(formData?.telemedicine_consultation_duration && formData?.telemedicine_consultation_price);
    const hasInPersonCheckbox = !!(additionalMultipleFields?.addInPersonConsultation);
    const hasTelemedicineCheckbox = !!(additionalMultipleFields?.addTelemedicineConsultation);
    const hasTelemedicine = hasInPersonConsultation || hasTelemedicineConsultation || hasInPersonCheckbox || hasTelemedicineCheckbox;

    // Check if only services are selected 
    const hasOnlyServices = () => {
        // Check if services are selected from additionalMultipleFields or formData
        const servicesFromAdditional = Array.isArray(additionalMultipleFields?.services) ? additionalMultipleFields.services : [];
        const servicesFromFormData = Array.isArray(formData?.services) ? formData.services : [];
        const currentServices = servicesFromAdditional.length > 0 ? servicesFromAdditional : servicesFromFormData;
        
        // Check if any valid services are selected
        const hasServices = currentServices.length > 0 && currentServices.some(service => {
            const serviceStr = String(service).trim();
            return serviceStr && serviceStr !== '' && serviceStr !== '0' && serviceStr !== 'null' && serviceStr !== 'undefined';
        });
        
        // Check if any consultation options are selected
        const hasInPersonConsultation = !!(formData?.in_person_consultation_duration && formData?.in_person_consultation_price);
        const hasTelemedicineConsultation = !!(formData?.telemedicine_consultation_duration && formData?.telemedicine_consultation_price);
        
        // Return true if only services are selected (no consultations)
        return hasServices && !hasInPersonConsultation && !hasTelemedicineConsultation;
    };

    const onlyServicesSelected = hasOnlyServices();
 
    return (
        <>
            {showDropDown && (
                <>
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                borderBottom: '1px solid',
                                borderColor: theme.colorTableHeaderBackground,
                                paddingBottom: '10px',
                                marginBottom: '12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%'
                            }}
                        >
                            <Box
                                sx={{
                                    color: theme.secondaryFontColor,
                                    fontWeight: 700,
                                    fontSize: '18px',
                                    fontFamily: 'Poppins'
                                }}
                            >
                                Providers Availability
                            </Box>
                            <Box
                                className={'primary-link cursor-pointer'}
                                onClick={() => {
                                    // Get current state values
                                    const currentCount = extraAvailabilityCount;
                                    const newCount = currentCount + 1;
                                    
                                    // Prepare new arrays with proper length
                                    const newWeekDays = Array.isArray(extraAvailabilityWeekDay) ? [...extraAvailabilityWeekDay] : [];
                                    const newStartTimes = Array.isArray(extraAvailabilityStartTime) ? [...extraAvailabilityStartTime] : [];
                                    const newEndTimes = Array.isArray(extraAvailabilityEndTime) ? [...extraAvailabilityEndTime] : [];
                                    const newLocations = Array.isArray(extraAvailabilityLocation) ? [...extraAvailabilityLocation] : [];
                                    const newTelemedicine = Array.isArray(extraAvailabilityTelemedecine) ? [...extraAvailabilityTelemedecine] : [];
                                    
                                    // Pad arrays to ensure they have enough elements
                                    while (newWeekDays.length <= newCount) newWeekDays.push('');
                                    while (newStartTimes.length <= newCount) newStartTimes.push('');
                                    while (newEndTimes.length <= newCount) newEndTimes.push('');
                                    while (newLocations.length <= newCount) newLocations.push('');
                                    while (newTelemedicine.length <= newCount) newTelemedicine.push(false);
                                    
                                    // Update all state at once
                                    setExtraAvailabilityCount(newCount);
                                    setExtraAvailabilityWeekDay(newWeekDays);
                                    setExtraAvailabilityStartTime(newStartTimes);
                                    setExtraAvailabilityEndTime(newEndTimes);
                                    setExtraAvailabilityLocation(newLocations);
                                    setExtraAvailabilityTelemedecine(newTelemedicine);
                                    
                                    // Update additionalMultipleFields to keep parent in sync
                                    setAdditionalMultipleFields(prev => ({
                                        ...prev,
                                        availability_day: newWeekDays,
                                        availability_start_time: newStartTimes,
                                        availability_end_time: newEndTimes,
                                        availability_location: newLocations,
                                        telemedicine: newTelemedicine,
                                        form_section: 'availability',
                                    }));
                                }}
                            >
                                + Add Another Availability
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                        <Typography variant="body2" style={{ color: '#999', fontSize: '14px', lineHeight: '1.5' }}>
                            Set the days and times when this provider is available for appointments. You can add multiple availability blocks to accommodate different schedules throughout the week.
                        </Typography>
                    </Grid>
                    {(() => {
                        const totalAvailabilities = extraAvailabilityCount + 1; // First availability + additional availabilities
                        const showRemoveLink = totalAvailabilities > 1;

                        const createRemoveHandler = (availabilityIndexToRemove) => {
                            if (!showRemoveLink) {
                                return null;
                            }

                            return () => {
                                const newWeekDays = [...extraAvailabilityWeekDay];
                                const newStartTime = [...extraAvailabilityStartTime];
                                const newEndTime = [...extraAvailabilityEndTime];
                                const newLocation = [...extraAvailabilityLocation];
                                const newTelemedicine = [...extraAvailabilityTelemedecine];

                                if (availabilityIndexToRemove === 0) {
                                    // Removing the first availability - shift all other availabilities up
                                    newWeekDays.shift();
                                    newStartTime.shift();
                                    newEndTime.shift();
                                    newLocation.shift();
                                    newTelemedicine.shift();

                                    setExtraAvailabilityWeekDay(newWeekDays);
                                    setExtraAvailabilityStartTime(newStartTime);
                                    setExtraAvailabilityEndTime(newEndTime);
                                    setExtraAvailabilityLocation(newLocation);
                                    setExtraAvailabilityTelemedecine(newTelemedicine);

                                    // Decrement count of additional availabilities
                                    if (extraAvailabilityCount > 0) {
                                        setExtraAvailabilityCount(extraAvailabilityCount - 1);
                                    }
                                } else {
                                    // Removing an additional availability (index > 0)
                                    const actualIndexToRemove = availabilityIndexToRemove;
                                    newWeekDays.splice(actualIndexToRemove, 1);
                                    newStartTime.splice(actualIndexToRemove, 1);
                                    newEndTime.splice(actualIndexToRemove, 1);
                                    newLocation.splice(actualIndexToRemove, 1);
                                    newTelemedicine.splice(actualIndexToRemove, 1);

                                    setExtraAvailabilityWeekDay(newWeekDays);
                                    setExtraAvailabilityStartTime(newStartTime);
                                    setExtraAvailabilityEndTime(newEndTime);
                                    setExtraAvailabilityLocation(newLocation);
                                    setExtraAvailabilityTelemedecine(newTelemedicine);

                                    // Decrement count of additional availabilities
                                    setExtraAvailabilityCount(extraAvailabilityCount - 1);
                                }

                                // Update additionalMultipleFields with the updated arrays
                                setAdditionalMultipleFields(prev => ({
                                    ...prev,
                                    availability_day: newWeekDays,
                                    availability_start_time: newStartTime,
                                    availability_end_time: newEndTime,
                                    availability_location: newLocation,
                                    telemedicine: newTelemedicine,
                                }));
                            };
                        };

                        return (
                            <>
                                {/* Render additional availabilities in reverse order (newest first) */}
                                {[...Array(extraAvailabilityCount)].reverse().map((_, reverseIndex) => {
                                    const index = extraAvailabilityCount - 1 - reverseIndex; // Convert reverse index back to original index
                                    const actualIndexToRemove = index + 1;
                                    return (
                                        <Availability
                                            key={index}
                                            count={index + 1}
                                            handleChange={handelAvailabilityChange(index + 1)}
                                            extraAvailabilityWeekDay={extraAvailabilityWeekDay}
                                            extraAvailabilityStartTime={extraAvailabilityStartTime}
                                            extraAvailabilityEndTime={extraAvailabilityEndTime}
                                            extraAvailabilityLocation={extraAvailabilityLocation}
                                            extraAvailabilityTelemedecine={extraAvailabilityTelemedecine}
                                            hasTelemedicine={hasTelemedicine}
                                            formData={formData}
                                            location={location}
                                            loading={loading}
                                            onlyServicesSelected={onlyServicesSelected}
                                            onRemoveAvailability={showRemoveLink ? createRemoveHandler(actualIndexToRemove) : null}
                                            onAddLocationClick={() => handleAddLocationClick(index + 1)}
                                        />
                                    );
                                })}
                                {/* Render first availability last */}
                                <Availability
                                    count={0}
                                    handleChange={handelAvailabilityChange(0)}
                                    extraAvailabilityWeekDay={extraAvailabilityWeekDay}
                                    extraAvailabilityStartTime={extraAvailabilityStartTime}
                                    extraAvailabilityEndTime={extraAvailabilityEndTime}
                                    extraAvailabilityLocation={extraAvailabilityLocation}
                                    extraAvailabilityTelemedecine={extraAvailabilityTelemedecine}
                                    hasTelemedicine={hasTelemedicine}
                                    formData={formData}
                                    location={location}
                                    loading={loading}
                                    onlyServicesSelected={onlyServicesSelected}
                                    onRemoveAvailability={showRemoveLink ? createRemoveHandler(0) : null}
                                    onAddLocationClick={() => handleAddLocationClick(0)}
                                />
                            </>
                        );
                    })()}
                </>
            )}
            
            {/* Add Location Popup */}
            <AddLocationPopup 
                open={openLocationPopup} 
                onClose={handleCloseLocationPopup}
            />
        </>
    );
}
